import React, { useCallback, useMemo, useState } from 'react';
import { Button, Grid, ISelectOption, SelectField } from 'ui-toolkit/components';
import { eCoverageType, prettyCoverageTypes } from 'ui-toolkit/enums/eCoverageType';
import { Form, Formik, useFormikContext } from 'formik';
import {
  defaultPolicyPremiumDetailsData,
  IUpdatePolicyPremiumDetailsRequest,
} from 'types/order/title/IPolicyPremiumDetailsResponse';
import { CoverageTypeFormValidation } from 'validation/order/title/PolicyPremiumDetailsFormsValidation';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../../types/shared/UUID';
import {
  useGetPolicyPremiumDetailsQuery,
  useUpdatePolicyPremiumDetailsMutation,
} from '../../../../../../API/order/title/PolicyPremiumDetailsAPI';
import { checkForMutationSuccess } from '../../../../../../API/checkForMutationSuccess';
import { ErrorMessageTW } from '../../../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { Show } from '../../../../../shared/Show';
import styled from 'styled-components';
import { LoadingSpinnerTW } from '../../../../../../tailwindComponents/loadingSpinnerTW/LoadingSpinnerTW';

const formOptions = (coverageTypes: eCoverageType[]): ISelectOption[] =>
  coverageTypes.map((coverageType) => ({ label: prettyCoverageTypes[coverageType], value: coverageType }));

export const PolicyTypesForm: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const [editForms, setEditForms] = useState<boolean>(() => false);

  const {
    data: {
      cashPurchase,
      ownerFormType,
      lenderFormType,
      availableLenderForms,
      availableOwnerForms,
    } = defaultPolicyPremiumDetailsData,
  } = useGetPolicyPremiumDetailsQuery(orderId);

  const [updatePolicyPremiumDetailsMutation, { error: updateError, isLoading }] =
    useUpdatePolicyPremiumDetailsMutation();

  const onUpdateTitlePolicyPremiumDetails = useCallback(
    async (updateRequest: IUpdatePolicyPremiumDetailsRequest) => {
      const result = await updatePolicyPremiumDetailsMutation({ orderId, updateRequest });
      checkForMutationSuccess(result, 'updated policy premium details');
    },
    [updatePolicyPremiumDetailsMutation, orderId]
  );

  const availableLenderFormOptions = useMemo(() => formOptions(availableLenderForms), [availableLenderForms]);
  const availableOwnerFormOptions = useMemo(() => formOptions(availableOwnerForms), [availableOwnerForms]);

  const onCancel = useCallback(() => {
    setEditForms(false);
  }, []);

  const initialValues = useMemo(() => ({ ownerFormType, lenderFormType }), [ownerFormType, lenderFormType]);

  return (
    <>
      <ErrorMessageTW rtkError={updateError} />
      <LoadingSpinnerTW isLoading={isLoading}>
        <Formik
          initialValues={initialValues}
          onSubmit={onUpdateTitlePolicyPremiumDetails}
          onReset={onCancel}
          validateOnMount
          validationSchema={CoverageTypeFormValidation}
          enableReinitialize
        >
          <Form>
            <Grid templateColumns={'3fr 1fr'} alignItems={'center'}>
              <SelectField50
                name={'ownerFormType'}
                placeholder={ownerFormType && prettyCoverageTypes[ownerFormType]}
                options={availableOwnerFormOptions}
                isDisabled={!editForms}
              />
              <EditCancelButtons editForms={editForms} setEditForms={setEditForms} />
              <Show when={!cashPurchase} fallback={<div />}>
                <SelectField50
                  name={'lenderFormType'}
                  placeholder={lenderFormType && prettyCoverageTypes[lenderFormType]}
                  options={availableLenderFormOptions}
                  isDisabled={!editForms}
                />
              </Show>
              <UpdateButton editForms={editForms} />
            </Grid>
          </Form>
        </Formik>
      </LoadingSpinnerTW>
    </>
  );
};

const SelectField50 = styled(SelectField)`
  height: 50px;
`;

const EditCancelButtons: React.FC<{
  editForms: boolean;
  setEditForms: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ editForms, setEditForms }) => {
  const buttonHandler = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setEditForms(true);
    },
    [setEditForms]
  );
  if (!editForms) {
    return (
      <Button type={'button'} intent={'secondary'} onClick={buttonHandler}>
        Edit
      </Button>
    );
  }
  return (
    <Button intent={'secondary'} type={'reset'}>
      Cancel
    </Button>
  );
};

const UpdateButton: React.FC<{ editForms: boolean }> = ({ editForms }) => {
  const { dirty, isValid, isSubmitting } = useFormikContext();
  if (!editForms) {
    return null;
  }
  return (
    <Button intent={'primary'} type={'submit'} disabled={(!dirty && isValid) || isSubmitting}>
      Update
    </Button>
  );
};
